import eq from "lodash-es/eq"

import { buildCssClassName, getTransitionTwClasses } from "@/3514/utils"

import { EParticipantHeaderVariant as EVariant } from "./participant.header.types"

const styles = {
    header: ({ variant }: { variant: EVariant }): string =>
        buildCssClassName(
            "bg-white [width:fill-available] relative",
            [eq(variant, EVariant.Default) && "z-[101] md:absolute flex justify-end"],
            [eq(variant, EVariant.Onboarding) && "px-[15px] md:px-[30px] py-[15px] h-[83px]"]
        ),
    desktop: {
        container: ({ isDropdownOpen }: { isDropdownOpen: boolean }): string =>
            buildCssClassName("relative w-full flex", isDropdownOpen ? "bg-blue-600 rounded-full" : "bg-transparent"),
        onboardingContainer: (): string => "relative w-full flex items-center justify-between",
        wiserLogo: (): string => "!cursor-default",
        logo: (): string => "w-fit translate-x-[10px] relative after:z-[-1] after:absolute after:inset-[-10px]",
        dropdownOpener: ({
            isDropdownOpen,
            isFakeParticipant,
            isRelative
        }: {
            isDropdownOpen: boolean
            isFakeParticipant: boolean
            isRelative: boolean
        }): string =>
            buildCssClassName(
                "p-[2px]",
                "md:p-[5px]",
                "rounded-full",
                "border-[5px]",
                "border-transparent",
                "hover:border-blue-600",
                "focus:border-blue-600",
                [isRelative || ["fixed", "right-[15px]"]],
                [isFakeParticipant ? "top-[50px]" : [isRelative || "top-[10px]"]],
                [isDropdownOpen ? ["bg-blue-600 z-[11]"] : "bg-transparent"],
                getTransitionTwClasses("colors", 100)
            ),
        list: ({
            isDropdownOpen,
            isFakeParticipant,
            isTriggerRelative
        }: {
            isDropdownOpen: boolean
            isFakeParticipant: boolean
            isTriggerRelative?: boolean
        }): string =>
            buildCssClassName(
                isDropdownOpen
                    ? [
                          "flex-col",
                          "flex",
                          "fixed",
                          "py-[15px]",
                          "px-[20px]",
                          "z-[10]",
                          "bg-blue-600",
                          "bottom-0",
                          "[left:auto]",
                          [isTriggerRelative ? "right-[30px]" : "right-[15px]"],
                          "rounded-[5px]",
                          "max-w-[140px]",
                          "w-full",
                          "h-min",
                          [isFakeParticipant ? "top-[75px]" : "top-[35px]"]
                      ]
                    : "hidden"
            ),
        listItem: ({ isMobileOnboarding }: { isMobileOnboarding: boolean }): string =>
            buildCssClassName(
                isMobileOnboarding ? "py-[8px] px-[15px] w-full flex justify-center" : "py-[10px]",
                "border-blue-400 border-b-[1px]"
            ),
        mobileListCloser: (): string =>
            buildCssClassName(
                "absolute",
                "right-[20px]",
                "top-[20px]",
                "border-1px",
                "border-transparent",
                "hover:border-gray-23",
                "focus:border-gray-23",
                getTransitionTwClasses("colors", 100)
            ),
        profileListTitle: ({ isMobile }: { isMobile: boolean } = { isMobile: false }): string =>
            buildCssClassName([isMobile && "text-center"], "mt-[10px]"),
        profileList: ({ isMobile }: { isMobile: boolean } = { isMobile: false }): string =>
            buildCssClassName(isMobile && "flex flex-col items-center gap-y-[5px]")
    },
    mobile: {
        container: ({
            isFakeParticipant,
            isOpenBurgerMenu
        }: {
            isFakeParticipant: boolean
            isOpenBurgerMenu: boolean
        }): string =>
            buildCssClassName(isFakeParticipant && "top-[7px]", [
                isOpenBurgerMenu
                    ? "justify-end absolute w-fit right-[20px] top-[20px]"
                    : "justify-between relative px-[20px] py-[20px] w-full flex items-center"
            ]),
        logo: (): string => "max-[767px]:ml-[5px]",
        button: (): string =>
            buildCssClassName(
                "w-[25px]",
                "h-[25px]",
                "z-[100]",
                "flex",
                "flex-col",
                "items-center",
                "justify-center",
                "rounded-full",
                "focus:outline-none",
                "relative",
                "before:absolute",
                "before:inset-[15px]"
            ),
        buttonLine1: ({ isOpenBurgerMenu, isAccent }: { isOpenBurgerMenu: boolean; isAccent: boolean }): string =>
            buildCssClassName(
                "block h-0.5 w-full rounded",
                isAccent ? "bg-accent" : "bg-blue-200",
                isOpenBurgerMenu && "translate-y-[6px] rotate-45",
                getTransitionTwClasses("[transform]", 200, "ease-in-out")
            ),
        buttonLine2: ({ isOpenBurgerMenu, isAccent }: { isOpenBurgerMenu: boolean; isAccent: boolean }): string =>
            buildCssClassName(
                "block h-0.5 w-full rounded my-1",
                isAccent ? "bg-accent" : "bg-blue-200",
                isOpenBurgerMenu && "opacity-0 scale-x-0",
                getTransitionTwClasses("all", 200, "ease-in-out")
            ),
        buttonLine3: ({ isOpenBurgerMenu, isAccent }: { isOpenBurgerMenu: boolean; isAccent: boolean }): string =>
            buildCssClassName(
                "block h-0.5 w-full rounded",
                isAccent ? "bg-accent" : "bg-blue-200",
                isOpenBurgerMenu && "-translate-y-[6px] -rotate-45",
                getTransitionTwClasses("[transform]", 200, "ease-in-out")
            )
    },
    mobileOnboarding: {
        container: (): string => "justify-between gap-x-[25px] relative w-full flex items-center",
        topContainer: (): string => "flex items-center justify-end p-[15px]",
        triggerWrapper: (): string => "p-[16px]",
        bottomContainer: (): string => "flex flex-col items-center justify-center px-[20px] pb-[30px]",
        avatar: (): string => "mb-[10px]",
        logo: (): string => "mx-[12px] my-[10px] !cursor-default",
        dropdownListContainer: ({
            isFakeParticipant,
            isDropdownOpen
        }: {
            isFakeParticipant: boolean
            isDropdownOpen: boolean
        }): string =>
            buildCssClassName(
                isDropdownOpen
                    ? [
                          isFakeParticipant ? "top-[7px]" : "top-[0px]",
                          ["fixed", "flex", "flex-col", "top-0", "right-[0px]", "w-full", "z-[102]", "bg-blue-500"]
                      ]
                    : "hidden"
            )
    }
} as const

export { styles as participantHeaderStyles }
